/* eslint-disable no-nested-ternary */
import { Room, ProductType, ProductTypeSchema } from '@reward-platform/ancillaries-schemas/common'
import { HotelDetailsResult, HotelSearchResult } from '@reward-platform/ancillaries-schemas/hotel'
import {
  CarHireDetailsDataTransferObject,
  CarHireSearchOffer,
} from '@reward-platform/ancillaries-schemas/car-hire'
import {
  pushEventToDataLayer,
  pushToDataLayer,
  DataLayerObject,
  compareAndPushEventInDataLayer,
  Navigation,
  HotelsFilterDataLayerObject,
  CarHireFilterDataLayerObject,
  ExperiencesFilterDataLayerObject,
  SerpFilterDataLayerObject,
  SearchState,
  toMemberId,
} from '~/utils/googleTagManager'
import {
  AirportLocation,
  CabinClass,
  CabinName,
  JourneyResult,
} from '@reward-platform/flights-schemas'
import { UserContextValue } from '~/context/user'
import { NextRouter } from 'next/router'
import { SelectedBound } from '~/components/search-and-results/FlightPriceFooter/BoundDetails/BoundDetails'
import { isProduction } from '~/utils/envChecks'
import { ExperienceSearchOffer } from '@reward-platform/ancillaries-schemas/experience/dtos/experience-search-dto'
import { getIsRefundable } from '@iagl/order-orders-cancellation-policies'
import { ExperienceDetailsDataTransferObject } from '@reward-platform/ancillaries-schemas/experience'
import { ReplaceExperienceItem } from '~/components/search-and-results/ReplaceItemModal/ReplaceItemModal.types'
import { Account } from '@reward-platform/ancillaries-schemas/accounts'
import { getAviosBasePathEnvValue, getOpCoMetaConfig, Iata } from '@reward-platform/platform-config'
import { FilterList } from '~/context/filters'
import {
  UpgradeOptionData,
  UpgradeOptionValueType,
} from '~/components/orders/UpgradeBasketItem/helpers/optionsBuilder'
import { gtmGA3, gtmGA4 } from './googleTagManager.types'
import { formatMinutesToHours } from '../datetime'
import formatAirportName from '../formatAirportName'
import { getGtmChannelByProductType, GTMChannels } from './googleTagManagerHelpers'
import { convertNameToId, getIntPrefix } from '../basketAnalytics'
import { getHotelPricingByLowestRoomRate } from '../pricing/extractHotelPricing'

const ga3ImpressionClick = 'ga3.impressionClick'
const ga3ImpressionView = 'ga3.impressionView'
const ga3Add = 'ga3.add'
const ga3Detail = 'ga3.detail'
const domainEnvironment = isProduction() ? 'prod' : 'non_prod'

export const getGAEventProgrammeCodeForOpCo = (iata: Iata) => {
  const opCoMeta = getOpCoMetaConfig(iata)
  return opCoMeta.id
}

export const journeyResultsInitialLoad = (
  journeys: JourneyResult[],
  userContext: UserContextValue,
  opCo: Iata,
  aviosBalance?: number
) => {
  const bookingCurrency = userContext.userCurrency
  const memberID = toMemberId(userContext.user?.memberIdentifier?.identifier)

  pushEventToDataLayer('view_item_list', {
    memberID,
    booking_currency: bookingCurrency,
    currency: bookingCurrency,
    balance: aviosBalance,
    domain_environment: domainEnvironment,
    programmeCode: getGAEventProgrammeCodeForOpCo(opCo),
    pageLocation: window.location.href,
    ecommerce: {
      items: journeys.map((journey, idx) => ({
        item_name: 'search',
        item_id: journey.flights[0].operatingCarrier.flightNumber,
        item_brand: journey.flights[0].operatingCarrier,
        item_category: GTMChannels.FLIGHTS,
        item_list_name: 'search',
        item_list_id: 'S1',
        index: idx + 1,
      })),
    },
  } as gtmGA3)
}

type JourneyDetailsInitialLoadPayload = {
  journey: JourneyResult
  origin: AirportLocation
  destination: AirportLocation
  userContext: UserContextValue
  cabins: Record<CabinName, CabinClass>
  opCo: Iata
  account?: Account
}

export const journeyDetailsInitialLoad = ({
  journey,
  opCo,
  userContext,
  account,
  origin,
  destination,
  cabins,
}: JourneyDetailsInitialLoadPayload) => {
  const [firstFlight] = journey.flights
  pushEventToDataLayer(ga3ImpressionClick, {
    event: 'navigation',
    nav_type: 'cta',
    nav_action: 'flight details',
    flight_from: `${formatAirportName(origin)}`,
    flight_to: `${formatAirportName(destination)}`,
    flight_code: `${firstFlight.operatingCarrier.carrier} ${firstFlight.operatingCarrier.flightNumber}`,
    flight_time: formatMinutesToHours(journey.totalDuration ?? 0),
    flight_route: `${journey.departureLocation}-${journey.arrivalLocation}`,
  } as DataLayerObject)

  const memberID = toMemberId(userContext?.user?.memberIdentifier?.identifier)

  const firstFlightValue =
    firstFlight.cabinClasses.Economy?.price ?? firstFlight.cabinClasses.Business?.price
  pushEventToDataLayer('view_item', {
    event: 'view_item',
    memberID,
    booking_currency: userContext.userCurrency,
    balance: account?.balance.amount,
    domain_environment: domainEnvironment,
    programmeCode: getGAEventProgrammeCodeForOpCo(opCo),
    pageLocation: window.location.href,
    collected_avios: account?.balance.amount,
    currency: userContext.userCurrency,
    value: firstFlightValue,
    avios_value: firstFlightValue,
    cash_value: 0,
    avios_percentage: 100,
    items: Object.keys(cabins)
      .map((cabinClassName, index) => {
        return {
          index: index + 1,
          item_name: `Details for flight ${firstFlight.operatingCarrier.carrier}${firstFlight.operatingCarrier.flightNumber}`,
          item_id: firstFlight.operatingCarrier.flightNumber,
          item_brand: firstFlight.operatingCarrier.carrier,
          item_category: 'flights',
          item_list_name: `Cabin ${cabinClassName}`,
          item_list_id: 'S1',
          price: firstFlight.cabinClasses[cabinClassName as CabinName]?.price ?? 0,
          quantity: 1,
        }
      })
      .filter((item) => item.price !== 0),
  })
}

export const journeySelectInitialLoad = (
  journey: JourneyResult,
  user: UserContextValue,
  url: string
) => {
  pushToDataLayer({
    event: 'Initial_setup',
    channel: 'flights',
    pageLocation: window.location.href,
    currency: user.userCurrency,
    referrer: url,
    departure_date: journey.departureTime.slice(0, 10),
    arrival_date: journey.arrivalTime.slice(0, 10),
    origin: journey.departureLocation,
    destination: journey.arrivalLocation,
  })
}

export const flightContinueButtonClickInitialLoad = (
  selectedFlights: SelectedBound[],
  totalFlightsPrice: number,
  cabinClasses: string[],
  user: UserContextValue,
  opCo: Iata,
  account?: Account
) => {
  const memberID = toMemberId(user?.user?.memberIdentifier?.identifier)

  pushEventToDataLayer('add_to_cart', {
    event: 'add_to_cart',
    memberID,
    booking_currency: user.userCurrency,
    currency: user.userCurrency,
    balance: account?.balance.amount,
    domain_environment: domainEnvironment,
    programmeCode: getGAEventProgrammeCodeForOpCo(opCo),
    pageLocation: window.location.href,
    value: totalFlightsPrice,
    avios_value: totalFlightsPrice,
    cash_value: 0,
    avios_percentage: 100,
    items: selectedFlights.map((journey, index) => {
      const journeyType = index === 0 ? 'outbound' : 'inbound'
      const dateOnly = journey?.departureTime.slice(0, 10)
      const cabinClassName = cabinClasses[index]
      const lowercaseCabinClassName = cabinClassName.toLowerCase()
      const capitalisedCabinClass = cabinClassName.charAt(0) + cabinClassName.slice(1).toLowerCase()
      const journeyAviosValue =
        journey?.flights.reduce((total, flight) => {
          const aviosValue = flight?.cabinClasses[capitalisedCabinClass as CabinName]?.price ?? 0
          return total + aviosValue
        }, 0) ?? 0
      const flightOperatingCarrier = journey?.flights
        .map((flight) => flight.operatingCarrier.carrier)
        .join('_')
      const flightOperatingCarrierNumber = journey?.flights
        .map((flight) => flight.operatingCarrier.flightNumber)
        .join('_')
      return {
        item_id: `${flightOperatingCarrier}${flightOperatingCarrierNumber}${dateOnly}${lowercaseCabinClassName}${journeyType}`,
        item_name: `${journeyType} flight ${flightOperatingCarrier}${flightOperatingCarrierNumber} with cabin ${capitalisedCabinClass}`,
        item_brand: flightOperatingCarrier,
        item_category: 'Flight selection',
        avios_value: journeyAviosValue,
        cash_value: 0,
        collectedAvios: 0,
        price: journeyAviosValue,
        avios_percentage: 100,
        quantity: 1,
      }
    }),
  })
}

export const hotelDetailsInitialLoad = (
  pushContextEventToDataLayer: (eventName: string, data?: DataLayerObject) => void,
  hotel: HotelDetailsResult
) => {
  const pricing = getHotelPricingByLowestRoomRate(hotel)

  pushContextEventToDataLayer(ga3Detail, {
    ecommerce: {
      detail: {
        actionField: {
          list: 'Search Results',
        },
        products: [
          {
            avios_value: pricing.aviosPrice,
            collected_avios: pricing.collectAmount,
            id: hotel?.vendorId,
            name: hotel?.vendorName,
            category: hotel?.category || 'Hotel',
            brand: hotel?.brand,
            price: pricing.cashPrice.toString(),
            variant: undefined,
          },
        ],
      },
    },
  } as gtmGA3)
  pushContextEventToDataLayer('', { ecommerce: null })
  pushContextEventToDataLayer('view_item', {
    ecommerce: {
      currency: hotel.rooms[0].roomRates[0].currencyCode,
      avios_value: pricing.aviosPrice,
      cash_value: pricing.cashPrice,
      collected_avios: 0,
      value: pricing.baseAmount,
      items: [
        {
          item_name: hotel.vendorName,
          item_id: convertNameToId(hotel.vendorName),
          item_brand: hotel.vendorName,
          item_category: hotel.category || 'Hotel',
          item_variant: hotel.rooms[0].name,
          item_list_name: 'Search',
          item_list_id: 'S1',
          price: pricing.baseAmount,
          cash_value: pricing.cashPrice,
          avios_value: pricing.aviosPrice,
          collected_avios: 0,
          index: 0,
          quantity: 1,
        },
      ],
    },
  } as gtmGA4)
}

export const carHireDetailsInitialLoad = (
  pushContextEventToDataLayer: (eventName: string, data?: DataLayerObject) => void,
  carHire: CarHireDetailsDataTransferObject
) => {
  pushContextEventToDataLayer(ga3Detail, {
    ecommerce: {
      detail: {
        actionField: {
          list: 'Search Results',
        },
        products: [
          {
            id: carHire?.result?.bundleId,
            name: carHire?.result?.name,
            variant: carHire?.result?.vehicleClassName,
            category: 'Car Hire',
            brand: carHire?.result?.vendorName.toUpperCase(),
            quantity: '1',
            avios_value: carHire?.result?.aviosPrice,
            collected_avios: carHire?.result?.collectAmount,
            price: carHire?.result?.cashPrice.toString(),
          },
        ],
      },
    },
  } as gtmGA3)
  pushContextEventToDataLayer('', { ecommerce: null })

  pushContextEventToDataLayer('view_item', {
    ecommerce: {
      currency: carHire.result.currencyCode,
      avios_value: carHire.result.aviosPrice,
      cash_value: carHire.result.cashPrice,
      collected_avios: 0,
      value: carHire.result.baseAmount ?? 0,
      items: [
        {
          item_name: carHire.result.name,
          item_id: convertNameToId(carHire.result.name),
          item_brand: carHire.result.vendorName,
          item_category: 'Car Hire',
          item_variant: carHire.result.vehicleClassName,
          item_list_name: 'Search',
          item_list_id: 'S1',
          price: carHire.result.baseAmount,
          cash_value: carHire.result.cashPrice,
          avios_value: carHire.result.aviosPrice,
          collected_avios: 0,
          index: 0,
          quantity: 1,
        },
      ],
    },
  } as gtmGA4)
}

export const hotelResultsInitialLoad = (
  pushContextEventToDataLayer: (eventName: string, data?: DataLayerObject) => void,
  hotels: HotelSearchResult[],
  currency: string
) => {
  pushContextEventToDataLayer(ga3ImpressionView, {
    ecommerce: {
      impressions: hotels.map((hotel: HotelSearchResult) => ({
        id: hotel.vendorId,
        name: hotel.vendorName,
        category: hotel.category || 'Hotel',
        position: hotels.indexOf(hotel) + 1,
      })),
    },
  } as gtmGA3)
  pushContextEventToDataLayer('', { ecommerce: null })
  pushContextEventToDataLayer('view_item_list', {
    ecommerce: {
      currency,
      items: hotels.map((hotel: HotelSearchResult) => {
        const pricing = getHotelPricingByLowestRoomRate(hotel)
        return {
          item_name: hotel.vendorName,
          item_id: convertNameToId(hotel.vendorName),
          item_brand: hotel.vendorName,
          item_category: hotel.category || 'Hotel',
          item_list_name: 'Search',
          item_list_id: 'S1',
          price: pricing.baseAmount,
          cash_value: pricing.cashPrice,
          avios_value: pricing.aviosPrice,
          collected_avios: 0,
          index: hotels.indexOf(hotel),
        }
      }),
    },
  } as gtmGA4)
}

export const carHireResultsInitialLoad = (
  pushContextEventToDataLayer: (eventName: string, data?: DataLayerObject) => void,
  carHires: CarHireSearchOffer[],
  currency: string
) => {
  pushContextEventToDataLayer(ga3ImpressionView, {
    ecommerce: {
      impressions: carHires.map((carHire: CarHireSearchOffer) => ({
        id: convertNameToId(carHire.name),
        name: carHire.name,
        variant: carHire.vehicleClassName,
        category: carHire.category || 'Car Hire',
        list: 'Search',
        position: carHires.indexOf(carHire) + 1,
      })),
    },
  } as gtmGA3)
  pushContextEventToDataLayer('', { ecommerce: null })
  pushContextEventToDataLayer('view_item_list', {
    ecommerce: {
      currency,
      items: carHires.map((carHire: CarHireSearchOffer) => ({
        item_name: carHire.name,
        item_id: convertNameToId(carHire.name),
        item_brand: carHire.vendorName,
        item_category: carHire.category || 'Car Hire',
        item_list_name: 'Search',
        item_list_id: 'S1',
        price: carHire.baseAmount,
        cash_value: carHire.cashPrice,
        avios_value: carHire.aviosPrice,
        collected_avios: 0,
        index: carHires.indexOf(carHire),
      })),
    },
  } as gtmGA4)
}

export const experienceResultsInitialLoad = (
  pushContextEventToDataLayer: (eventName: string, data?: DataLayerObject) => void,
  experiences: ExperienceSearchOffer[],
  currency: string
) => {
  pushContextEventToDataLayer('', { ecommerce: null })
  pushContextEventToDataLayer('view_item_list', {
    ecommerce: {
      currency,
      items: experiences.map((experience: ExperienceSearchOffer) => ({
        item_name: experience?.name,
        item_id: convertNameToId(experience.name),
        item_brand: experience?.vendorName,
        item_category: 'Experience',
        item_list_name: 'Search',
        item_list_id: 'S1',
        price: experience.cashPrice,
        cash_value: experience.cashPrice,
        avios_value: experience.aviosPrice,
        collected_avios: 0,
        index: experiences.indexOf(experience),
      })),
    },
  } as gtmGA4)
}

export function getDestinationFullName(queryString: string) {
  const encodedDestination = queryString
    .split('destination%255BfullName%255D%3D')[1]
    ?.split('%26')[0]
  return decodeURIComponent(decodeURIComponent(encodedDestination))
}

export const experienceBookNowButton = (
  pushContextEventToDataLayer: (eventName: string, data?: DataLayerObject) => void,
  experience: ExperienceDetailsDataTransferObject['result'],
  fromDate: string,
  toDate: string,
  queryString: string,
  title: string,
  travellerCount?: number
) => {
  pushContextEventToDataLayer('navigation', {
    nav_type: 'cta',
    nav_action: 'book now',
    avios_value: 0,
    cash_value: 0,
    ticket_option: title ?? experience.name,
    traveller_count: travellerCount,
    from_date: fromDate,
    to_date: toDate,
    destination: getDestinationFullName(queryString),
  })
}

export const trackCheckAvailability = (
  pushContextEventToDataLayer: (eventName: string, data?: DataLayerObject) => void,
  fromDate: string,
  toDate: string,
  queryString: string
) => {
  pushContextEventToDataLayer('navigation', {
    nav_type: 'cta',
    nav_action: 'check availability',
    from_date: fromDate,
    to_date: toDate,
    destination: getDestinationFullName(queryString),
  })
}

export const backToExperience = (
  pushContextEventToDataLayer: (eventName: string, data?: DataLayerObject) => void,
  fromDate: string,
  toDate: string
) => {
  pushContextEventToDataLayer('navigation', {
    nav_type: 'cta',
    nav_action: 'See all experiences',
    from_date: fromDate,
    to_date: toDate,
  })
}

export const experienceDetailsLoad = (
  pushContextEventToDataLayer: (eventName: string, data?: DataLayerObject) => void,
  experience: ExperienceDetailsDataTransferObject['result'],
  travellerCount: number,
  bookingCurrency?: string
) => {
  pushContextEventToDataLayer('', { ecommerce: null })

  pushContextEventToDataLayer('view_item', {
    ecommerce: {
      currency: bookingCurrency || 'not_applicable',
      avios_value: experience.aviosPrice,
      cash_value: experience.cashPrice,
      collected_avios: 0,
      value: experience.cashPrice,
      items: [
        {
          item_name: experience?.name,
          item_id: convertNameToId(experience.name),
          item_brand: experience?.vendorName,
          item_category: 'Experience',
          item_variant: null,
          item_list_name: 'Search',
          item_list_id: 'S1',
          duration: `${experience.experienceDuration?.duration} minutes`,
          traveler_count: travellerCount,
          price: experience.cashPrice,
          cash_value: experience.cashPrice,
          avios_value: experience.aviosPrice,
          collected_avios: 0,
          quantity: 1,
          index: 0,
        },
      ],
    },
  })
}

export const trackExperienceAddToBasket = (
  pushContextEventToDataLayer: (eventName: string, data?: DataLayerObject) => void,
  bookingCurrency: string,
  experience: ExperienceDetailsDataTransferObject['result'],
  travellerCount: number,
  title: string,
  slectedItem?: ReplaceExperienceItem
) => {
  pushContextEventToDataLayer('', { ecommerce: null })

  pushContextEventToDataLayer('add_to_cart', {
    ecommerce: {
      currency: bookingCurrency ?? 'GBP',
      avios_value: slectedItem?.paymentMethods?.aviosAmount ?? 0,
      cash_value: slectedItem?.paymentMethods?.cashAmount ?? 0,
      collected_value: 0,
      value: slectedItem?.paymentMethods?.cashAmount ?? 0,
      items: [
        {
          item_name: experience.name ?? '',
          item_id: convertNameToId(experience.name),
          item_brand: experience.vendorName,
          item_category: 'Experience',
          item_variant: title ?? experience.name,
          item_list_name: 'Search',
          item_list_id: 'S1',
          duration: `${experience.experienceDuration?.duration} minutes`,
          traveller_count: travellerCount,
          price: slectedItem?.paymentMethods?.cashAmount ?? 0,
          quantity: 1,
          index: 0,
        },
      ],
    },
  } as gtmGA4)
}

export const trackKeepOrReplaceExperience = (
  pushContextEventToDataLayer: (eventName: string, data?: DataLayerObject) => void,
  experienceName: string,
  navAction: string,
  travellerCount: number,
  toDate: string,
  queryString: string
) => {
  pushContextEventToDataLayer('navigation', {
    nav_type: 'cta',
    nav_action: navAction,
    avios_value: 0,
    cash_value: 0,
    ticket_option: experienceName,
    traveller_count: travellerCount,
    to_date: toDate,
    destination: getDestinationFullName(queryString),
  })
}

export const hotelResultsCardClick = (
  pushContextEventToDataLayer: (eventName: string, data?: DataLayerObject) => void,
  hotel: HotelSearchResult
) => {
  const pricing = getHotelPricingByLowestRoomRate(hotel)

  pushContextEventToDataLayer(ga3ImpressionClick, {
    ecommerce: {
      click: {
        actionField: {
          list: 'Search',
        },
        products: [
          {
            avios_value: pricing.aviosPrice,
            collected_avios: pricing.collectAmount,
            id: hotel.vendorId,
            name: hotel.vendorName,
            category: hotel.category || 'Hotel',
            price: pricing.cashPrice.toString(),
          },
        ],
      },
    },
  } as gtmGA3)
  pushContextEventToDataLayer('', { ecommerce: null })

  pushContextEventToDataLayer('select_item', {
    ecommerce: {
      currency: hotel.currencyCode,
      items: [
        {
          item_name: hotel.vendorName,
          item_id: convertNameToId(hotel.vendorName),
          item_brand: hotel.vendorName,
          item_category: hotel.category || 'Hotel',
          item_list_name: 'Search',
          item_list_id: 'S1',
          price: pricing.baseAmount,
          cash_value: pricing.cashPrice,
          avios_value: pricing.aviosPrice,
          collected_avios: 0,
          index: 0,
        },
      ],
    },
  } as gtmGA4)
}

export const carHireResultsCardClick = (
  pushContextEventToDataLayer: (eventName: string, data?: DataLayerObject) => void,
  carHire: CarHireSearchOffer,
  position: number | undefined
) => {
  pushContextEventToDataLayer(ga3ImpressionClick, {
    ecommerce: {
      click: {
        actionField: {
          list: 'Search',
        },
        products: [
          {
            id: convertNameToId(carHire.name),
            name: carHire.name,
            variant: carHire.vehicleClassName,
            category: carHire.category || 'Car Hire',
            list: 'Search',
            avios_value: carHire.aviosPrice,
            collected_avios: carHire.collectAmount,
            position: position ? position + 1 : undefined,
          },
        ],
      },
    },
  } as gtmGA3)
  pushContextEventToDataLayer('', { ecommerce: null })

  pushContextEventToDataLayer('select_item', {
    ecommerce: {
      currency: carHire.currencyCode,
      items: [
        {
          item_name: carHire.name,
          item_id: convertNameToId(carHire.name),
          item_brand: carHire.vendorName,
          item_category: carHire.category || 'Car Hire',
          item_list_name: 'Search',
          item_list_id: 'S1',
          price: carHire.baseAmount,
          cash_value: carHire.cashPrice,
          avios_value: carHire.aviosPrice,
          collected_avios: 0,
          index: 0,
        },
      ],
    },
  } as gtmGA4)
}

export const hotelAddToBasket = (
  pushContextEventToDataLayer: (eventName: string, data?: DataLayerObject) => void,
  hotel: HotelDetailsResult,
  room: Room,
  roomRate: string
) => {
  const selectedRate = room.roomRates.find((rate) => rate.id === roomRate)
  if (selectedRate) {
    pushContextEventToDataLayer(ga3Add, {
      ecommerce: {
        position: hotel.rooms.indexOf(room) + 1,
        add: {
          actionField: {
            list: 'Search',
          },
          products: [
            {
              avios_value: selectedRate.aviosPrice,
              collected_avios: selectedRate.collectAmount,
              id: hotel.vendorId,
              name: hotel.vendorName,
              category: hotel.category || 'Hotel',
              brand: hotel.brand,
              price: selectedRate.cashPrice.toString(),
              variant: room.name,
              amenities: selectedRate.optionInfo,
              room_size: room.dimensions?.sqMeters,
              refundable: getIsRefundable(selectedRate.packageRules).toString(),
              bed_size: room.maxOccupants,
            },
          ],
        },
      },
    } as gtmGA3)
    pushContextEventToDataLayer('', { ecommerce: null })

    const analyticsObject = {
      item_name: hotel.vendorName,
      item_id: convertNameToId(hotel.vendorName),
      item_brand: hotel.vendorName,
      item_category: hotel.category || 'Hotel',
      item_variant: room.name,
      item_list_name: 'Search',
      item_list_id: 'S1',
      index: 0,
      room_capacity: room.maxOccupants,
      refundable: getIsRefundable(selectedRate.packageRules) ? 'Refundable' : 'Non-Refundable',
      bed_size: room.beds[0].title,
      cash_value: selectedRate.cashPrice,
      avios_value: selectedRate.aviosPrice,
      collected_avios: selectedRate.collectAmount,
      price: selectedRate.baseAmount,
      quantity: 1,
    }

    const getFormattedAmenities = () => {
      const { optionInfo } = selectedRate
      const formattedAmenities: { [key: string]: string } = {}

      optionInfo?.forEach((amenity: string, i) => {
        formattedAmenities[`amenities.${i}`] = amenity
      })

      return formattedAmenities
    }

    pushContextEventToDataLayer('add_to_cart', {
      ecommerce: {
        currency: selectedRate.currencyCode,
        avios_value: selectedRate.aviosPrice,
        cash_value: selectedRate.cashPrice,
        collected_avios: selectedRate.collectAmount,
        value: selectedRate.baseAmount,
        items: [{ ...analyticsObject, ...getFormattedAmenities() }],
      },
    })
  }
}
export const carHireAddToBasket = (
  pushContextEventToDataLayer: (eventName: string, data?: DataLayerObject) => void,
  carHire: CarHireDetailsDataTransferObject
) => {
  pushContextEventToDataLayer(ga3Add, {
    ecommerce: {
      add: {
        actionField: {
          list: 'Search',
        },
        products: [
          {
            id: carHire?.result?.bundleId,
            name: carHire?.result?.name,
            variant: carHire?.result?.vehicleClassName,
            category: 'Car Hire',
            brand: carHire?.result?.vendorName.toUpperCase(),
            quantity: '1',
            price: carHire?.result?.cashPrice.toString(),
            avios_value: carHire?.result?.aviosPrice,
            collected_avios: carHire?.result?.collectAmount,
            seats: carHire?.result?.seats,
            transmission: carHire?.result?.vehicleTransmission,
            mileage_allowance: '',
            door_count: carHire?.result?.doors,
            air_con: carHire?.result?.aircon,
            luggage_capacity: carHire?.result?.bags,
            insurance_cover: '',
            additional_driver: '',
            child_seat: '',
          },
        ],
      },
    },
  } as gtmGA3)
  pushContextEventToDataLayer('', { ecommerce: null })

  pushContextEventToDataLayer('add_to_cart', {
    ecommerce: {
      currency: carHire?.result?.currencyCode,
      avios_value: carHire?.result?.aviosPrice,
      cash_value: carHire?.result?.cashPrice,
      collected_avios: carHire?.result?.collectAmount,
      value: carHire?.result?.baseAmount,
      items: [
        {
          item_name: carHire?.result?.name,
          item_id: convertNameToId(carHire?.result?.name),
          item_brand: carHire?.result?.vendorName,
          item_category: 'Car Hire',
          item_variant: carHire?.result?.vehicleClassName,
          item_list_name: 'Search',
          item_list_id: 'S1',
          index: 0,
          seats: getIntPrefix(carHire?.result?.seats),
          transmission: carHire?.result?.vehicleTransmission,
          mileage_allowance: carHire?.result?.unlimitedMileage ? 'unlimited' : 'limited',
          door_count: getIntPrefix(carHire?.result?.doors),
          luggage_capacity: carHire?.result?.bags,
          air_con: carHire?.result?.aircon,
          cash_value: carHire?.result?.cashPrice,
          avios_value: carHire?.result?.aviosPrice,
          collected_avios: carHire?.result?.collectAmount,
          price: carHire?.result?.baseAmount,
          quantity: 1,
        },
      ],
    },
  } as gtmGA4)
}

/** Type-safe function for returning the string or concatenating multiple string values */
const concatenateValues = (value?: string | string[]): string => {
  if (!value) {
    return ''
  }
  if (Array.isArray(value)) {
    return value.join(',').toLowerCase()
  }
  return value.toLowerCase()
}

const formatPriceRangeAnalyticsValue = (priceRange?: string | string[]): string =>
  Array.isArray(priceRange) ? `min - ${priceRange[0]} | max - ${priceRange[1]}` : ''

const findOptionName = (
  key: string,
  selectedFilters: Record<string, string | string[]>,
  filters: FilterList
): string => {
  const selectedOptions = Array.isArray(selectedFilters[key])
    ? selectedFilters[key]
    : selectedFilters[key]
    ? [selectedFilters[key]]
    : []
  const optionsNames = filters?.find((x) => x.key === key) ?? null
  const names =
    optionsNames === null
      ? []
      : selectedOptions.map(
          (selectedElement) => optionsNames.options?.find((x) => x.key === selectedElement)?.name
        )

  return names.filter(Boolean).join(',').toLowerCase()
}

const getHotelFiltersAnalyticsObject = (
  filters: FilterList,
  selectedFilters: Record<string, string | string[]>
): HotelsFilterDataLayerObject => {
  const { guestRating, starRatings, priceRange } = selectedFilters
  return {
    property_type: findOptionName('propertyTypes', selectedFilters, filters),
    guest_rating: concatenateValues(guestRating) === '0' ? 'any' : concatenateValues(guestRating),
    star_rating: concatenateValues(starRatings),
    meal_plans: findOptionName('mealPlans', selectedFilters, filters),
    amenities: findOptionName('amenities', selectedFilters, filters),
    price_range: formatPriceRangeAnalyticsValue(priceRange),
    accessibility: findOptionName('accessibilities', selectedFilters, filters),
  }
}

const getCarFiltersAnalyticsObject = (
  filters: FilterList,
  selectedFilters: Record<string, string | string[]>
): CarHireFilterDataLayerObject => {
  const { fuelType, carSize, priceRange } = selectedFilters
  return {
    fuel_type: fuelType ? concatenateValues(fuelType) : null,
    gearbox: findOptionName('transmissionType', selectedFilters, filters),
    vendor: findOptionName('carCompany', selectedFilters, filters),
    price_range: formatPriceRangeAnalyticsValue(priceRange),
    size: carSize?.length ? findOptionName('carSize', selectedFilters, filters) : null,
  }
}

const getExperiencesFiltersAnalyticsObject = (
  filters: FilterList,
  selectedFilters: Record<string, string | string[]>,
  extra: any
): ExperiencesFilterDataLayerObject => {
  const { priceRange } = selectedFilters
  return {
    specials: findOptionName('specials', selectedFilters, filters),
    price_range: formatPriceRangeAnalyticsValue(priceRange),
    duration: findOptionName('timeWindow', selectedFilters, filters),
    accessibility: findOptionName('accessibilities', selectedFilters, filters),
  }
}

export const pushFiltersToDataLayer = (
  pushContextEventToDataLayer: (eventName: string, data?: DataLayerObject) => void,
  productType: ProductType,
  filters: FilterList,
  selectedFilters: Record<string, string | string[]>,
  searchState: SearchState,
  extra: any
) => {
  const baseNavigationDataLayerObject = {
    nav_type: 'cta',
    nav_action: 'apply filters',
    search_state: searchState,
  }
  const baseSerpFilterDataLayerObject: SerpFilterDataLayerObject = {
    channel: getGtmChannelByProductType(productType),
    search_state: searchState,
    product: 'reward_platform',
  }
  switch (productType) {
    case ProductTypeSchema.enum.HOTEL:
      {
        const hotelObject = getHotelFiltersAnalyticsObject(filters, selectedFilters)
        pushContextEventToDataLayer('navigation', {
          ...baseNavigationDataLayerObject,
          ...hotelObject,
        })
        pushContextEventToDataLayer('serp_filter', {
          ...baseSerpFilterDataLayerObject,
          ...hotelObject,
        })
      }
      break
    case ProductTypeSchema.enum.CAR_HIRE:
      {
        const carObject = getCarFiltersAnalyticsObject(filters, selectedFilters)
        pushContextEventToDataLayer('navigation', {
          ...baseNavigationDataLayerObject,
          ...carObject,
        })
        pushContextEventToDataLayer('serp_filter', {
          ...baseSerpFilterDataLayerObject,
          ...carObject,
        })
      }
      break
    case ProductTypeSchema.enum.EXPERIENCE:
      {
        const experienceObject = getExperiencesFiltersAnalyticsObject(
          filters,
          selectedFilters,
          extra
        )
        pushContextEventToDataLayer('navigation', {
          ...baseNavigationDataLayerObject,
          ...experienceObject,
        })
        pushContextEventToDataLayer('serp_filter', {
          ...baseSerpFilterDataLayerObject,
          ...experienceObject,
        })
      }
      break
    default:
      break
  }
}

export const pushSortToDataLayer = (
  pushContextEventToDataLayer: (eventName: string, data?: DataLayerObject) => void,
  productType: ProductType,
  filters: FilterList,
  selectedFilters: Record<string, string | string[]>,
  selectedSortOption: string,
  extra: any,
  searchState: 'initial' | 'refined'
) => {
  const sortOptionMapping: Record<string, string> = {
    RECOMMENDED: 'recommended',
    'PRICE.ASC': 'price low to high',
    'PRICE.DESC': 'price high to low',
    'STAR_RATING.ASC': 'star rating low to high',
    'STAR_RATING.DESC': 'star rating high to low',
  }

  const sortLabel = sortOptionMapping[selectedSortOption]
  const baseObject = {
    nav_type: 'cta',
    nav_action: 'sort by',
    sort_setting: sortLabel,
    sort_state: 'refined',
  }

  pushContextEventToDataLayer('serp_sort', {
    channel: getGtmChannelByProductType(productType),
    product: 'reward_platform',
    search_state: searchState,
    sort_setting: sortLabel,
  })

  switch (productType) {
    case ProductTypeSchema.enum.HOTEL:
      {
        const hotelObject = getHotelFiltersAnalyticsObject(filters, selectedFilters)
        pushContextEventToDataLayer('navigation', {
          ...baseObject,
          ...hotelObject,
        })
      }
      break
    case ProductTypeSchema.enum.CAR_HIRE:
      {
        const carObject = getCarFiltersAnalyticsObject(filters, selectedFilters)
        pushContextEventToDataLayer('navigation', {
          ...baseObject,
          ...carObject,
        })
      }
      break
    case ProductTypeSchema.enum.EXPERIENCE:
      {
        const experienceObject = getExperiencesFiltersAnalyticsObject(
          filters,
          selectedFilters,
          extra
        )
        pushContextEventToDataLayer('navigation', {
          ...baseObject,
          ...experienceObject,
        })
      }
      break
    default:
      break
  }
}

export const pushEditToDataLayer = (
  pushContextEventToDataLayer: (eventName: string, data?: DataLayerObject) => void,
  productType: ProductType,
  filters: FilterList,
  selectedFilters: Record<string, string | string[]>,
  extra?: any
) => {
  const baseObject = {
    nav_type: 'cta',
    nav_action: 'edit',
  }

  switch (productType) {
    case ProductTypeSchema.enum.HOTEL:
      {
        const hotelObject = getHotelFiltersAnalyticsObject(filters, selectedFilters)
        pushContextEventToDataLayer('navigation', {
          ...baseObject,
          ...hotelObject,
        })
      }
      break
    case ProductTypeSchema.enum.CAR_HIRE:
      {
        const carObject = getCarFiltersAnalyticsObject(filters, selectedFilters)
        pushContextEventToDataLayer('navigation', {
          ...baseObject,
          ...carObject,
        })
      }
      break
    case ProductTypeSchema.enum.EXPERIENCE:
      {
        const experienceObject = getExperiencesFiltersAnalyticsObject(
          filters,
          selectedFilters,
          extra
        )
        pushContextEventToDataLayer('navigation', {
          ...baseObject,
          ...experienceObject,
        })
      }
      break
    default:
      break
  }
}

type Flight = {
  origin?: AirportLocation
  destination?: AirportLocation
  returnDate?: string
  departureDate: string
  oneWayOnly: boolean
  passengerCount: number
  adultCount: number
  childCount: number
  childrenAges: number[]
}

export const pushSearchEventData = (
  userContext: UserContextValue,
  opCo: Iata,
  flight: Flight,
  allFlights: number,
  account?: Account
) => {
  const memberID = toMemberId(userContext.user?.memberIdentifier?.identifier)
  const bookingCurrency = userContext.userCurrency
  const userBalance = account?.balance.amount
  const { origin, destination, departureDate, returnDate, passengerCount } = flight

  pushEventToDataLayer('search', {
    memberID,
    booking_currency: bookingCurrency,
    currency: bookingCurrency,
    balance: userBalance,
    domain_environment: domainEnvironment,
    programmeCode: getGAEventProgrammeCodeForOpCo(opCo),
    channel: 'flights',
    pageLocation: '/flights',
    booking_start_location: `${origin?.cityName}, ${origin?.countryCode}`,
    booking_end_location: `${destination?.cityName}, ${destination?.countryCode}`,
    from_date: departureDate,
    to_date: returnDate,
    traveller_count: passengerCount,
    search_state: 'initial',
    result_count: allFlights,
  })
}

export const pushSearchFlightEventData = (
  userContext: UserContextValue,
  opCo: Iata,
  flight: Flight,
  allFlights: number,
  account?: Account
) => {
  const memberID = toMemberId(userContext.user?.memberIdentifier?.identifier)
  const bookingCurrency = userContext.userCurrency
  const userBalance = account?.balance.amount
  const { adultCount, childCount, childrenAges, origin, destination, departureDate, returnDate } =
    flight

  const youngAdultsForAnalytics = childrenAges.filter((age) => age > 15)

  const adultCountForAnalytics = adultCount + youngAdultsForAnalytics.length
  const childCountForAnalytics = childCount - youngAdultsForAnalytics.length

  const updatedChildrenAgesForAnalytics = childrenAges.filter((age) => age <= 15)
  const childrenAgesNode = {
    childrenAges:
      childCount > 0
        ? updatedChildrenAgesForAnalytics.map((childAge, index) => ({
            child: `{child_${index + 1}}`,
            age: childAge,
          }))
        : [],
  }

  pushEventToDataLayer('search', {
    memberID,
    booking_currency: bookingCurrency,
    currency: bookingCurrency,
    balance: userBalance,
    domain_environment: domainEnvironment,
    programmeCode: getGAEventProgrammeCodeForOpCo(opCo),
    channel: 'flights',
    pageLocation: '/flights',
    booking_start_location: `${origin?.cityName}, ${origin?.countryCode}`,
    booking_end_location: `${destination?.cityName}, ${destination?.countryCode}`,
    departure_date: departureDate,
    arrival_date: returnDate,
    flightsNumber: allFlights || 0, // if {0} means no results
    adults: adultCountForAnalytics,
    children: childCountForAnalytics,
    traveller_count: adultCountForAnalytics + childCountForAnalytics,
    ...childrenAgesNode,
  })
}

export const gtmSearchSubmit = (searchType: string) =>
  pushEventToDataLayer('navigation', { nav_type: 'cta', nav_action: `search ${searchType}` })

export const refineSearchSessionStorage = (routerPathname: string) => {
  const basePath = getAviosBasePathEnvValue()
  switch (routerPathname) {
    case '/hotels/results':
    case `${basePath}/hotels/results`:
      sessionStorage.setItem('clicked_back_hotels', 'true')
      break
    case '/cars/results':
    case `${basePath}/cars/results`:
      sessionStorage.setItem('clicked_back_car_hire', 'true')
      break
    case '/experiences/results':
    case `${basePath}/experiences/results`:
      sessionStorage.setItem('clicked_back_experiences', 'true')
      break
    default:
  }
}

export const flightPageDetails = (
  router: NextRouter,
  userContext: UserContextValue,
  aviosBalance?: number,
  flightDetails?: Record<string, unknown>
) => {
  const bookingCurrency = userContext.userCurrency
  const memberID = toMemberId(userContext.user?.memberIdentifier?.identifier)

  const routerDetails = {
    origin: router.query.fromAirportCode,
    destination: router.query.toAirportCode,
    outbound_date: concatenateValues(router.query.departureDate),
    return_date: concatenateValues(router.query.returnDate),
    referrer: router.route,
  }

  return {
    memberID,
    booking_currency: bookingCurrency,
    currency: bookingCurrency,
    balance: aviosBalance,
    ...routerDetails,
    ...flightDetails,
  }
}

export const sendHotelUpgradeComponentCtaEvent = (action: string) => {
  compareAndPushEventInDataLayer('navigation', {
    nav_element: 'confirm upgrade',
    nav_type: 'cta',
    nav_action: action,
  } as Navigation)
}

export const sendHotelUpgradeComponentOptionsEvent = (
  options: UpgradeOptionData[],
  isRefundable: boolean,
  hasBreakfast: boolean
) => {
  const UPGRADE_ACTION: Record<UpgradeOptionValueType, string> = {
    breakfastAndRefundable: '3 all upgrade options available',
    refundable: '2 last min cancellation upgrade available',
    breakfast: '1 breakfast upgrade available',
  }

  const eligibleKeys: UpgradeOptionValueType[] = [
    ...(!isRefundable && !hasBreakfast ? ['breakfastAndRefundable' as const] : []),
    ...(!isRefundable ? ['refundable' as const] : []),
    ...(!hasBreakfast ? ['breakfast' as const] : []),
  ]

  const match = eligibleKeys.find((key) => options.some((o) => o.value === key))
  if (!match) {
    return
  }

  compareAndPushEventInDataLayer('navigation', {
    nav_element: 'upgrade options',
    nav_type: 'banner',
    nav_action: UPGRADE_ACTION[match],
  } as Navigation)
}
